import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Circle, Mail, Wallet, UserPlus, MailCheck, X, Sparkles, PlayCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import api from "@/lib/api";
import WelcomeTour, { shouldAutoStartTour } from "@/components/WelcomeTour";

const DISMISS_KEY = "ndis_setup_dismissed";
const CELEBRATED_KEY = "ndis_setup_celebrated";

const STEPS = {
  email:    { icon: Mail,       title: "Connect your email",          desc: "So invoices and incident alerts can be sent.", to: "/help#email-setup",  cta: "Set up email" },
  budget:   { icon: Wallet,     title: "Set your NDIS budget",        desc: "Enter your plan total and dates.",            to: "/budget",            cta: "Set budget" },
  worker:   { icon: UserPlus,   title: "Add a support worker",        desc: "Add their details so you can log shifts.",    to: "/workers",           cta: "Add worker" },
  register: { icon: MailCheck,  title: "Invite them to register",     desc: "Share the app URL — they sign in as a worker.",to: "/help#add-worker",   cta: "Show me how" },
};

export default function SetupChecklistCard() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/setup-checklist");
        if (!alive) return;
        setData(data);
        // Fire one-time celebration when the last step ticks over to done.
        if (data.complete && localStorage.getItem(CELEBRATED_KEY) !== "1") {
          localStorage.setItem(CELEBRATED_KEY, "1");
          setTimeout(() => celebrateSetup(), 250);
        }
        // Auto-start the welcome tour on first view (only when checklist still showing).
        if (!data.complete && shouldAutoStartTour()) {
          setTimeout(() => setTourOpen(true), 700);
        }
      } catch { /* fail-soft — checklist is non-critical */ }
    })();
    return () => { alive = false; };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  if (!data || data.complete || dismissed) return null;

  const pct = Math.round((data.done / data.total) * 100);

  return (
    <>
      <Card
        data-testid="setup-checklist-card"
        className="rounded-3xl border-2 p-6 sm:p-7 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, hsl(var(--client) / 0.06), hsl(var(--brand-lime) / 0.10))" }}
      >
        <div className="blob animate-blob" style={{ width: 200, height: 200, top: -60, right: -40, background: "hsl(var(--client) / 0.18)" }} />
        <button
          onClick={dismiss}
          data-testid="setup-checklist-dismiss"
          aria-label="Dismiss setup checklist"
          className="absolute top-4 right-4 w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:bg-muted/60 transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-10 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-client/15 ring-1 ring-client/25 grid place-items-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-client" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-client">Get started</p>
              <h2 className="text-xl sm:text-2xl font-display font-black tracking-tight">Welcome to your hub! Let's get you set up.</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {data.done} of {data.total} complete — finish these to unlock the full app.{" "}
                <button
                  onClick={() => setTourOpen(true)}
                  data-testid="setup-take-tour-btn"
                  className="text-client font-bold underline-offset-2 hover:underline inline-flex items-center gap-1"
                >
                  <PlayCircle className="w-3.5 h-3.5" /> Take the tour
                </button>
              </p>
            </div>
          </div>

          <Progress value={pct} className="h-2.5 rounded-full" data-testid="setup-checklist-progress" />

          <div className="grid sm:grid-cols-2 gap-2.5 pt-1">
            {data.items.map((item) => (
              <Step key={item.id} id={item.id} done={item.done} />
            ))}
          </div>
        </div>
      </Card>
      <WelcomeTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </>
  );
}

function Step({ id, done }) {
  const cfg = STEPS[id];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Link
      to={cfg.to}
      data-testid={`setup-step-${id}`}
      className={`group rounded-2xl border-2 p-3.5 flex items-center gap-3 transition-all card-lift ${
        done
          ? "bg-emerald-50/70 dark:bg-emerald-950/20 border-emerald-300/50 dark:border-emerald-700/30"
          : "bg-card hover:border-client/40 border-border"
      }`}
    >
      {done ? (
        <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0" data-testid={`setup-step-${id}-done`} />
      ) : (
        <Circle className="w-6 h-6 text-muted-foreground flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold leading-tight flex items-center gap-1.5 ${done ? "line-through text-muted-foreground" : ""}`}>
          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
          {cfg.title}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 truncate">{cfg.desc}</p>
      </div>
      {!done && (
        <Button size="sm" variant="outline" tabIndex={-1}
                className="rounded-full font-bold text-[11px] h-7 px-3 border-2 group-hover:border-client group-hover:text-client whitespace-nowrap">
          {cfg.cta} →
        </Button>
      )}
    </Link>
  );
}


// =====================================================================
// One-time celebration when all 4 steps tick over to done.
// =====================================================================
function celebrateSetup() {
  // Brand-coloured confetti burst from the bottom-centre of the viewport.
  const colors = ["#5DD3B0", "#7B5EA7", "#EE82A2", "#F0C75E", "#8AD7C0"];
  const fire = (originX, particleRatio, opts) => {
    confetti({
      origin: { x: originX, y: 0.85 },
      colors,
      particleCount: Math.floor(180 * particleRatio),
      spread: 90,
      startVelocity: 45,
      ticks: 220,
      ...opts,
    });
  };
  fire(0.15, 0.30, { angle: 75 });
  fire(0.85, 0.30, { angle: 105 });
  fire(0.5,  0.40, { angle: 90, spread: 120, scalar: 1.1 });

  toast.success("You're all set up! Time to create your first timesheet.", {
    duration: 6000,
    icon: "🎉",
    action: {
      label: "Create timesheet →",
      onClick: () => { window.location.href = "/timesheets/new"; },
    },
  });
}
